import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  booleanCellValues,
  emptyCellOneLevel,
  formatCurrencyCell,
  formatDateCell,
  formatDateWithTimeCell,
  formatPercentageCell,
  hasPermission,
} from '@/shared';
import { useUpdatePromocion } from '@/shared/actions/erp';
import { SwitchComponente } from '@/shared/components/ui';
import { TipoDescuento, type IPromocion } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';

export const useColumnsPromociones = () => {
  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* mutations -----------------------
  const updSate = useUpdatePromocion({
    enableRedirect: false,
  });

  const promocionesColumns = useMemo<MRT_ColumnDef<IPromocion>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      /* {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */

      {
        accessorKey: 'tipo_descuento',
        header: 'Tipo Descuento',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_descuento'),
      },
      {
        accessorKey: 'valor_descuento',
        header: 'Valor Descuento',
        size: 200,
        Cell: ({ row }) => {
          const tipoDescuento = row.original?.tipo_descuento;

          if (tipoDescuento === TipoDescuento.PORCENTAJE) {
            return formatPercentageCell(row, 'valor_descuento');
          }

          return formatCurrencyCell(row, 'valor_descuento');
        },
      },

      {
        accessorKey: 'fecha_inicio',
        header: 'Fecha Inicio',
        size: 200,
        Cell: ({ row }) => formatDateCell(row, 'fecha_inicio'),
      },
      {
        accessorKey: 'fecha_fin',
        header: 'Fecha Fin',
        size: 200,
        Cell: ({ row }) => formatDateCell(row, 'fecha_fin'),
      },

      {
        accessorKey: 'indefinido',
        header: 'Indefinido',
        size: 200,
        Cell: ({ row }) => booleanCellValues(row, 'indefinido'),
      },

      {
        accessorKey: 'prioridad',
        header: 'Prioridad',
        size: 200,
      },

      {
        accessorKey: 'recurrencia',
        header: 'Recurrencia',
        size: 200,
      },

      // state ---------
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_promocion))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_promocion!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  const promocionesOnlyViewColumns = useMemo<MRT_ColumnDef<IPromocion>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      },

      {
        accessorKey: 'tipo_descuento',
        header: 'Tipo Descuento',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_descuento'),
      },
      {
        accessorKey: 'valor_descuento',
        header: 'Valor Descuento',
        size: 200,
        Cell: ({ row }) => {
          const tipoDescuento = row.original?.tipo_descuento;

          if (tipoDescuento === TipoDescuento.PORCENTAJE) {
            return formatPercentageCell(row, 'valor_descuento');
          }

          return formatCurrencyCell(row, 'valor_descuento');
        },
      },

      // {
      //   accessorKey: 'fecha_inicio',
      //   header: 'Fecha Inicio',
      //   size: 200,
      //   Cell: ({ row }) => formatDateCell(row, 'fecha_inicio'),
      // },
      // {
      //   accessorKey: 'fecha_fin',
      //   header: 'Fecha Fin',
      //   size: 200,
      //   Cell: ({ row }) => formatDateCell(row, 'fecha_fin'),
      // },

      // {
      //   accessorKey: 'indefinido',
      //   header: 'Indefinido',
      //   size: 200,
      //   Cell: ({ row }) => booleanCellValues(row, 'indefinido'),
      // },
    ],
    []
  );

  return {
    promocionesColumns,
    promocionesOnlyViewColumns,
  };
};
